// Tạo snapshot backup trước khi uninstall. Folder: ~/.avatar/uninstall-backups/
// <project-name>-<ts>/ với cấu trúc trong spec doc.
import { cp, mkdir, readFile, writeFile } from "node:fs/promises";
import { homedir } from "node:os";
import { basename, join } from "node:path";
import type { AvatarProjectArtifacts } from "./detect-avatar-project-artifacts.js";

export interface BackupManifest {
  projectName: string;
  projectPath: string;
  timestamp: string;
  avatarVersion: string;
  artifacts: {
    claudeDir: boolean;
    claudeMd: boolean;
    postMergeHook: boolean;
    prePushHook: boolean;
  };
  // Tên repo trong src/ có pre-push hook Avatar (v3 layout, ticket #129 phase-03).
  // Ghi lúc snapshot = "có hook LÚC NÀY"; PHẢI reconcile lại sau khi pruner chạy
  // xong bằng reconcileV3PrePushHooksBackupManifest — xem hàm đó để biết lý do.
  v3PrePushHooks: string[];
  // Ticket #138 phase-04 — 2 field RIÊNG, CẤM gộp thành 1 (N3): thư mục
  // <backupDir>/repo-ignores/ chứa bản sao của HAI pruner khác nhau
  // (`exclude-<repo>-<ts>` ← info/exclude · `gitignore-<repo>-<ts>` ← .gitignore).
  // Gộp 1 field ⇒ không truy ngược được artifact nào thuộc pruner nào, và
  // reconcile của pruner này xoá nhầm mục của pruner kia. Cùng luật reconcile
  // như v3PrePushHooks: ghi lúc snapshot = "có block LÚC NÀY", phải reconcile
  // sau khi pruner chạy xong.
  excludeArtifacts: string[];
  gitignoreArtifacts: string[];
}

const UNINSTALL_BACKUPS_DIR = join(homedir(), ".avatar", "uninstall-backups");

export interface CreateUninstallBackupSnapshotOptions {
  // Tên repo (từ findReposWithAvatarPrePushHook) sắp bị pruneStaleAvatarPrePushHooks
  // gỡ — dùng để quyết định có tạo hooksBackupDir hay không (v3 hooks luôn có
  // artifacts.postMergeHook/prePushHook === null nên không thể dựa vào 2 field đó).
  v3PrePushRepos?: string[];
  // Ticket #138 phase-04 — tên repo còn Avatar block ở <gitdir>/info/exclude
  // (excludeRepos) / ở .gitignore (gitignoreRepos), sắp bị 2 pruner dọn. Dùng
  // để quyết định có tạo <backupDir>/repo-ignores/ hay không. Giữ RIÊNG 2 danh
  // sách (N3) — cùng lý do 2 field manifest riêng.
  excludeRepos?: string[];
  gitignoreRepos?: string[];
}

export async function createUninstallBackupSnapshot(
  projectRoot: string,
  artifacts: AvatarProjectArtifacts,
  avatarVersion: string,
  opts?: CreateUninstallBackupSnapshotOptions,
): Promise<string> {
  const projectName = basename(projectRoot);
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
  const backupDir = join(UNINSTALL_BACKUPS_DIR, `${projectName}-${timestamp}`);
  const v3PrePushRepos = opts?.v3PrePushRepos ?? [];
  const excludeRepos = opts?.excludeRepos ?? [];
  const gitignoreRepos = opts?.gitignoreRepos ?? [];

  await mkdir(backupDir, { recursive: true, mode: 0o700 });

  // Copy .claude/ và CLAUDE.md nếu tồn tại.
  if (artifacts.claudeDir) {
    await cp(artifacts.claudeDir, join(backupDir, ".claude"), { recursive: true });
  }
  if (artifacts.claudeMd) {
    await cp(artifacts.claudeMd, join(backupDir, "CLAUDE.md"));
  }

  // Copy hooks legacy (v2) sang backup/hooks/. Hook v3 (src/<repo>/.git/hooks/pre-push)
  // KHÔNG copy ở đây — pruneStaleAvatarPrePushHooks tự copy vào cùng thư mục này
  // (nhận backupDir = <backupPath>/hooks) để chỉ có MỘT nơi implement copy/xoá
  // (DRY, phase-02 §Security). Điều kiện tạo thư mục phải tính CẢ v3: 2 field
  // legacy luôn null ở v3 (bug gốc ticket #129) — nếu chỉ gate theo chúng thì
  // hooksBackupDir không bao giờ được tạo, pruner xoá thật mà backup rỗng.
  const hasLegacyHook = !!(artifacts.postMergeHook || artifacts.prePushHook);
  const hasV3PrePushRepos = v3PrePushRepos.length > 0;
  if (hasLegacyHook || hasV3PrePushRepos) {
    const hooksBackupDir = join(backupDir, "hooks");
    await mkdir(hooksBackupDir, { recursive: true });
    if (artifacts.postMergeHook) {
      await cp(artifacts.postMergeHook, join(hooksBackupDir, "post-merge"));
    }
    if (artifacts.prePushHook) {
      await cp(artifacts.prePushHook, join(hooksBackupDir, "pre-push"));
    }
  }

  // Thư mục chung cho 2 pruner ignore (#138 phase-04) — đối xứng hooks/ ở trên:
  // pruner TỰ copy vào đây (nhận backupDir = <backupPath>/repo-ignores) để chỉ
  // có MỘT nơi implement copy/ghi (DRY). Tạo sẵn khi CÓ repo cần dọn ở BẤT KỲ
  // pruner nào — cả 2 dùng chung thư mục, phân biệt bằng tiền tố tên file.
  if (excludeRepos.length > 0 || gitignoreRepos.length > 0) {
    await mkdir(join(backupDir, "repo-ignores"), { recursive: true });
  }

  // Write manifest.
  const manifest: BackupManifest = {
    projectName,
    projectPath: projectRoot,
    timestamp,
    avatarVersion,
    artifacts: {
      claudeDir: !!artifacts.claudeDir,
      claudeMd: !!artifacts.claudeMd,
      postMergeHook: !!artifacts.postMergeHook,
      prePushHook: !!artifacts.prePushHook,
    },
    v3PrePushHooks: v3PrePushRepos,
    excludeArtifacts: excludeRepos,
    gitignoreArtifacts: gitignoreRepos,
  };
  await writeFile(join(backupDir, "manifest.json"), JSON.stringify(manifest, null, 2), "utf8");

  return backupDir;
}

// Ghi đè field v3PrePushHooks trong manifest.json SAU KHI pruner chạy xong.
// createUninstallBackupSnapshot chạy TRƯỚC pruner nên danh sách nó ghi chỉ là
// "repo có hook LÚC SNAPSHOT" — repo rơi vào bucket `failed` (backup lỗi, pruner
// không xoá, không có file backup) vẫn nằm trong đó nếu không reconcile, khiến
// blueprint restore trỏ tới file không tồn tại trong <backupDir>/hooks/. Chỉ
// giữ lại tên repo ĐÃ BACKUP THÀNH CÔNG (result.backups — tập con 1-1 của
// `pruned`, phase-02 §Danh pháp bucket).
export async function reconcileV3PrePushHooksBackupManifest(
  backupDir: string,
  backedUpRepoNames: string[],
): Promise<void> {
  const manifestPath = join(backupDir, "manifest.json");
  const raw = await readFile(manifestPath, "utf8");
  const manifest = JSON.parse(raw) as BackupManifest;
  manifest.v3PrePushHooks = backedUpRepoNames;
  await writeFile(manifestPath, JSON.stringify(manifest, null, 2), "utf8");
}

// HAI hàm reconcile RIÊNG cho 2 pruner ignore (#138 phase-04, N3) — CẤM gộp
// thành một. Cả hai ghi vào CÙNG thư mục <backupDir>/repo-ignores/ nhưng mỗi
// hàm chỉ được đụng field của mình: gộp lại thì tập kết quả của pruner này ghi
// đè field của pruner kia ⇒ manifest liệt kê sai artifact nào thuộc ai, và
// blueprint restore trỏ tới file không tồn tại. Cùng lý do reconcile như
// v3PrePushHooks: chỉ giữ tên repo ĐÃ BACKUP THÀNH CÔNG (result.backups), repo
// rơi bucket `failed` không có file thật trong repo-ignores/.
export async function reconcileExcludeBackupManifest(
  backupDir: string,
  backedUpRepoNames: string[],
): Promise<void> {
  const manifestPath = join(backupDir, "manifest.json");
  const raw = await readFile(manifestPath, "utf8");
  const manifest = JSON.parse(raw) as BackupManifest;
  manifest.excludeArtifacts = backedUpRepoNames;
  await writeFile(manifestPath, JSON.stringify(manifest, null, 2), "utf8");
}

export async function reconcileGitignoreBackupManifest(
  backupDir: string,
  backedUpRepoNames: string[],
): Promise<void> {
  const manifestPath = join(backupDir, "manifest.json");
  const raw = await readFile(manifestPath, "utf8");
  const manifest = JSON.parse(raw) as BackupManifest;
  manifest.gitignoreArtifacts = backedUpRepoNames;
  await writeFile(manifestPath, JSON.stringify(manifest, null, 2), "utf8");
}
